import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '..';
import swal from 'sweetalert';

const SignInLayer = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    portal: 'customer', // Default to customer portal
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Validate form inputs
  const validateForm = () => {
    let isValid = true;
    const newErrors = { email: '', password: '' };

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email address is required';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Handle portal selection
  const handlePortalChange = (e) => {
    setFormData((prev) => ({ ...prev, portal: e.target.value }));
  };

  const SubmitLogin = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const headersList = {
        Accept: '*/*',
        'Content-Type': 'application/json',
      };
      const bodyContent = JSON.stringify({
        email: formData.email,
        password: formData.password,
      });

      const url = formData.portal === 'admin' ? `${API_URL}/api/user/login` : `${API_URL}/api/affiliates/login`;

      const response = await fetch(url, {
        method: 'POST',
        body: bodyContent,
        headers: headersList,
      });

      const data = await response.json();

      if (response.status === 200) {
        if (formData.portal === 'admin') {
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('activeTab', 'main');
        } else {
          const dataD = {
            ...data?.affiliate,
            role: 'owner',
          };
          localStorage.setItem('activeTab', 'company');
          localStorage.setItem('user', JSON.stringify(dataD));
        }
        window.location.href = '/';
      } else if (response.status === 401) {
        swal('Information Error', 'Invalid Email or Password', 'error');
      } else {
        swal('Error', 'Error while signing in', 'error');
      }
    } catch (error) {
      console.error('Login error:', error);
      swal('Error', 'An unexpected error occurred. Please try again.', 'error');
    }
  };

  // Check if form is valid to enable/disable submit button
  const isFormValid = () => {
    return (
      formData.email &&
      emailRegex.test(formData.email) &&
      formData.password &&
      formData.password.length >= 8
    );
  };

  return (
    <section className="auth bg-base d-flex flex-wrap">
      <div className="auth-left d-lg-block d-none">
        <div className="d-flex align-items-center flex-column h-100 justify-content-center">
          <img src="assets/images/signin.webp" alt="Sign In Illustration" />
        </div>
      </div>
      <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
        <div className="max-w-464-px mx-auto w-100">
          <div>
            <Link to="/" className="mb-40 max-w-290-px">
              <img
                style={{ background: 'black', padding: 10, borderRadius: 10 }}
                src="assets/images/logo.png"
                alt="Logo"
              />
            </Link>
            <h4 className="mb-12">Sign In to your Account</h4>
            <p className="mb-32 text-secondary-light text-lg">
              Welcome back! Please enter your details
            </p>
          </div>
          <form onSubmit={SubmitLogin}>
            <div className="d-flex align-items-center flex-wrap gap-24 mb-20">
              <div className="bg-primary-50 px-20 py-12 radius-8">
                <span className="form-check checked-primary d-flex align-items-center gap-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="radio200"
                    id="radio100"
                    value="customer"
                    checked={formData.portal === 'customer'}
                    onChange={handlePortalChange}
                  />
                  <label
                    className="form-check-label line-height-1 fw-medium text-secondary-light"
                    htmlFor="radio100"
                  >
                    Customer Portal
                  </label>
                </span>
              </div>
              <div className="bg-neutral-100 px-20 py-12 radius-8">
                <span className="form-check checked-success d-flex align-items-center gap-2">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="radio200"
                    id="radio200"
                    value="admin"
                    checked={formData.portal === 'admin'}
                    onChange={handlePortalChange}
                  />
                  <label
                    className="form-check-label line-height-1 fw-medium text-secondary-light"
                    htmlFor="radio200"
                  >
                    Administrative Portal
                  </label>
                </span>
              </div>
            </div>
            <div className="icon-field mb-16">
              <span className="icon top-50 translate-middle-y">
                <Icon icon="mage:email" />
              </span>
              <input
                type="email"
                name="email"
                className="form-control h-56-px bg-neutral-50 radius-12"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
              />
              {errors.email && (
                <span className="text-danger text-sm mt-2">{errors.email}</span>
              )}
            </div>
            <div className="position-relative mb-20">
              <div className="icon-field">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  id="your-password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
              </div>
              <span
                onClick={() => setShowPassword(!showPassword)}
                className={
                  showPassword
                    ? 'toggle-password ri-eye-off-line cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light'
                    : 'toggle-password ri-eye-line cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light'
                }
                data-toggle="#your-password"
              />
              {errors.password && (
                <span className="text-danger text-sm mt-2">{errors.password}</span>
              )}
            </div>
            <div className="mb-20">
              <div className="d-flex justify-content-between gap-2">
                <div className="form-check style-check d-flex align-items-center">
                  <input
                    className="form-check-input border border-neutral-300"
                    type="checkbox"
                    id="remember"
                  />
                  <label className="form-check-label" htmlFor="remember">
                    Remember me
                  </label>
                </div>
              {
                formData.portal == 'customer' ? 
                <div>
                  <strong>

                  <Link style={{color:'#191950'}} to={'/forgot-password'}>Forgot Password? </Link>
                  </strong>
                </div>: null

              }
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
              disabled={!isFormValid()}
              style={{
                opacity: isFormValid() ? 1 : 0.6,
                cursor: isFormValid() ? 'pointer' : 'not-allowed',
              }}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
      <style jsx>{`
        .text-danger {
          color: red;
          font-size: 12px;
          margin-top: 4px;
          display: block;
        }
      `}</style>
    </section>
  );
};

export default SignInLayer;